import React, { useEffect, useState } from "react";
import { MapPin, UserX, Loader2, AlertTriangle } from "lucide-react";

const GRACE_MIN = 3;
const CHARGE_PER_MIN = 2;
const NO_SHOW_MIN = 10;

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

// Live "driver has arrived — waiting" indicator. taxi=true shows the 3-min free-wait
// countdown and then the ₹2/min waiting-charge notice; otherwise it's an informational
// count-up timer (food / goods). compact renders the smaller driver-side badge. When
// onNoShow is passed (driver side, taxi/goods), a "Rider no-show — cancel" action appears
// once the driver has waited NO_SHOW_MIN minutes.
export const ArrivedWaiting = ({ arrivedAt, taxi = false, compact = false, who = "Your driver", onNoShow, noShowBusy = false, graceReminder = false, testid = "arrived-waiting" }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!arrivedAt) return null;
  const elapsed = Math.max(0, Math.floor((now - new Date(arrivedAt).getTime()) / 1000));
  const graceLeft = GRACE_MIN * 60 - elapsed;
  const noShowLeft = NO_SHOW_MIN * 60 - elapsed;

  let label;
  let warn = false;
  if (taxi) {
    if (graceLeft > 0) {
      label = compact ? `Arrived · free wait ${fmt(graceLeft)}` : `${who} has arrived · free wait ${fmt(graceLeft)}`;
    } else {
      warn = true;
      label = compact ? `Waiting · ₹${CHARGE_PER_MIN}/min (${fmt(-graceLeft)} over)` : `${who} is waiting · ₹${CHARGE_PER_MIN}/min charge now applies (${fmt(-graceLeft)} over)`;
    }
  } else {
    label = compact ? `Arrived · waiting ${fmt(elapsed)}` : `${who} has arrived · waiting ${fmt(elapsed)}`;
  }

  const tone = warn ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800";
  const size = compact ? "px-3 py-1.5 text-xs" : "px-4 py-3 text-sm";
  const badge = (
    <div className={`inline-flex items-center gap-2 rounded-xl font-bold ${tone} ${size}`} data-testid={testid}>
      <MapPin className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} /> {label}
    </div>
  );

  if (!onNoShow) {
    if (graceReminder && elapsed >= (NO_SHOW_MIN - 2) * 60) {
      return (
        <div className="flex flex-col items-start gap-2">
          {badge}
          <div className="inline-flex items-center gap-2 rounded-xl bg-amber-100 px-4 py-3 text-sm font-bold text-amber-800" data-testid="grace-reminder">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {noShowLeft > 0 ? `${who} may cancel in ${fmt(noShowLeft)} — please reach your pickup` : `${who} may leave at any moment — please reach your pickup`}
          </div>
        </div>
      );
    }
    return badge;
  }

  return (
    <div className="flex flex-col items-start gap-2">
      {badge}
      {noShowLeft > 0 ? (
        <p className="text-[11px] text-ink-muted" data-testid="no-show-countdown">You can report a rider no-show in {fmt(noShowLeft)}</p>
      ) : (
        <button type="button" onClick={onNoShow} disabled={noShowBusy} className="inline-flex items-center gap-1.5 rounded-full border-2 border-red-500 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50 disabled:opacity-60" data-testid="ride-no-show-cancel">
          {noShowBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserX className="h-3.5 w-3.5" />} Rider no-show — cancel
        </button>
      )}
    </div>
  );
};
